import { FC, RefObject, useEffect, useState } from 'react';

import { FloatingFocusManager, FloatingPortal, useFloatingRootContext } from '@floating-ui/react';
import { autoUpdate, useFloating } from '@floating-ui/react-dom';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { getPurpurIcon } from '@telialt/common-icon/src/utils';
import { isSSR } from '@telialt/utils/src/helpers';
import { useComponentId, useResolution } from '@telialt/utils/src/hooks';
import { isDesktopRes } from '@telialt/utils/src/responsive';

import { useDropdownOpenClose } from '../../hooks/useDropdownOpenClose';
import { useKeyboardFocus } from '../../hooks/useKeyboardFocus';
import { UserMenuItemType } from '../../model';
import { NAVIGATION_TEST_IDS } from '../../test-ids';
import { MenuItemIcon } from '../MenuItem/MenuItem.styles';
import { UserButton, UserIconWrapper, UserMenuList } from './MenuItemUser.styles';
import { UserMenuListItem } from './UserMenuListItem';

export const UserMenu: FC<{ menuItems: UserMenuItemType[] }> = ({ menuItems }) => {
    const { t } = useTranslations();
    const [open, setOpen] = useState<false | string>(false);
    const userPurpurIcon = getPurpurIcon('end-user');
    const menuId = useComponentId('user-menu');
    const title = t['navbar.user.authorized'];
    const isOpen = open === title;
    const isDesktop = useResolution(() => !isSSR() && isDesktopRes());

    const { refs, floatingStyles, elements } = useFloating({
        placement: 'bottom-end',
        whileElementsMounted: autoUpdate,
        open: isOpen,
    });

    const context = useFloatingRootContext({
        elements: {
            reference: elements.reference as Element,
            floating: elements.floating,
        },
        open: isOpen,
    });

    const buttonRef = refs.reference as unknown as RefObject<HTMLButtonElement>;
    const menuRef = refs.floating as unknown as RefObject<HTMLDivElement>;
    useKeyboardFocus(elements.floating);

    const { handleToggle } = useDropdownOpenClose({
        open: isOpen,
        onOpenChange: () => setOpen((prev) => (prev === title ? false : title)),
        buttonRef,
        menuRef,
    });

    useEffect(() => {
        if (!isDesktop && isOpen) {
            setOpen(false);
        }
    }, [isDesktop, isOpen]);

    return (
        <>
            <UserButton
                ref={refs.setReference}
                onClick={handleToggle}
                aria-haspopup="true"
                aria-expanded={isOpen}
                aria-controls={menuId}
                $isActive={isOpen}
                data-test={NAVIGATION_TEST_IDS.USER}
                aria-hidden={false}
            >
                <UserIconWrapper>
                    <MenuItemIcon svg={userPurpurIcon} size="sm" />
                </UserIconWrapper>
                {title}
            </UserButton>
            <FloatingFocusManager context={context} guards={false} order={['floating']}>
                {isOpen && (
                    <FloatingPortal>
                        <UserMenuList
                            ref={refs.setFloating}
                            aria-label={title}
                            id={menuId}
                            style={floatingStyles}
                            data-test={NAVIGATION_TEST_IDS.USER_MENU}
                            role="menu"
                        >
                            {menuItems.map((item) => (
                                <UserMenuListItem key={item.id} menuItem={item} />
                            ))}
                        </UserMenuList>
                    </FloatingPortal>
                )}
            </FloatingFocusManager>
        </>
    );
};
